import { useState, useEffect } from "react";
import { Box, Button, Checkbox, Dialog, HStack, Portal, Text, VStack, CloseButton, Spinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { SiGooglesheets } from "react-icons/si";
import EmailTagsInput from "@/components/jobOffer/ui/EmailTagsInput";
import { SpreadsheetIntegrationService } from "@/services/SpreadsheetIntegrationService";

const GoogleSheetsIntegrationModal = ({ isOpen, onClose, workflowId, integration, onSuccess }) => {
    const { t } = useTranslation("models");
    const isEditMode = integration !== null && integration !== undefined;

    const [emails, setEmails] = useState([]);
    const [originalEmails, setOriginalEmails] = useState([]);
    const [includeInNotifications, setIncludeInNotifications] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [emailsError, setEmailsError] = useState(false);
    const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (isEditMode) {
                const initial = integration.accessEmails ?? [];
                setEmails(initial);
                setOriginalEmails(initial.map((e) => e.toLowerCase()));
                setIncludeInNotifications(integration.includeInNotifications ?? false);
            } else {
                setEmails([]);
                setOriginalEmails([]);
                setIncludeInNotifications(false);
            }
            setError(null);
            setEmailsError(false);
            setIsDisconnectOpen(false);
        }
    }, [isOpen, isEditMode, integration]);

    const handleSubmit = async () => {
        if (emails.length === 0) {
            setEmailsError(true);
            return;
        }
        setError(null);
        setIsLoading(true);
        try {
            if (isEditMode) {
                const lowerEmails = emails.map((e) => e.toLowerCase());
                const addedEmails = emails.filter((e) => !originalEmails.includes(e.toLowerCase()));
                const removedEmails = originalEmails.filter((e) => !lowerEmails.includes(e));
                await SpreadsheetIntegrationService.updateIntegration(workflowId, integration.id, {
                    addedEmails,
                    removedEmails,
                    includeInNotifications,
                });
            } else {
                await SpreadsheetIntegrationService.createGoogleSheets(workflowId, {
                    accessEmails: emails,
                    includeInNotifications,
                });
            }
            onSuccess();
        } catch (err) {
            const status = err?.response?.status;
            if (status === 502) {
                setError(t("recruitmentDetailPage.googleSheetsModal.errorGoogleApi"));
            } else {
                setError(t("recruitmentDetailPage.googleSheetsModal.errorGoogleApi"));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        setIsDisconnecting(true);
        try {
            await SpreadsheetIntegrationService.deleteIntegration(workflowId, integration.id, true);
            onSuccess();
        } finally {
            setIsDisconnecting(false);
            setIsDisconnectOpen(false);
        }
    };

    const m = t("recruitmentDetailPage.googleSheetsModal", { returnObjects: true });

    return (
        <>
            <Dialog.Root open={isOpen} onOpenChange={(e) => { if (!e.open) onClose(); }}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner p={4}>
                        <Dialog.Content
                            bg="bg.card"
                            color="white"
                            borderColor="border.primary"
                            border="1px solid"
                            w={{ base: "calc(100vw - 2rem)", md: "lg" }}
                            maxW="100%"
                            maxH="calc(100dvh - 2rem)"
                            overflowY="auto"
                            borderRadius={12}
                        >
                            <Dialog.Body pt={6} pb={6} px={6}>
                                <HStack gap={2} mb={5}>
                                    <SiGooglesheets size={22} color="#34A853" />
                                    <Text fontWeight={700} fontSize="lg">
                                        {isEditMode ? m.titleEdit : m.titleCreate}
                                    </Text>
                                </HStack>

                                {isEditMode && (
                                    <VStack align="flex-start" gap={1} mb={4} p={3} bg="bg.secondary" borderRadius={8}>
                                        <HStack gap={2}>
                                            <Text fontSize="sm" color="text.grey">{m.connectedAt}:</Text>
                                            <Text fontSize="sm">
                                                {new Date(integration.createdAt).toLocaleDateString("pl-PL")}
                                            </Text>
                                        </HStack>
                                        <HStack gap={2}>
                                            <a
                                                href={integration.spreadsheetUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: "#34A853", fontSize: "var(--chakra-fontSizes-sm)", display: "flex", alignItems: "center", gap: 4 }}
                                            >
                                                <SiGooglesheets size={14} />
                                                {m.spreadsheetLink}
                                            </a>
                                        </HStack>
                                    </VStack>
                                )}

                                <VStack align="flex-start" gap={1} mb={4} w="full">
                                    <Text fontSize="sm" color="text.grey">{m.accessEmailsLabel}</Text>
                                    <EmailTagsInput
                                        values={emails}
                                        onChange={(v) => { setEmails(v); if (v.length > 0) setEmailsError(false); }}
                                        placeholder={m.accessEmailsPlaceholder}
                                        borderColor={emailsError ? "red.400" : "border.primary"}
                                    />
                                    {emailsError && (
                                        <Text fontSize="xs" color="red.400">{m.accessEmailsRequired}</Text>
                                    )}
                                </VStack>

                                <Box mb={5}>
                                    <Checkbox.Root
                                        checked={includeInNotifications}
                                        onCheckedChange={(e) => setIncludeInNotifications(!!e.checked)}
                                    >
                                        <Checkbox.HiddenInput />
                                        <Checkbox.Control
                                            borderColor="border.primary"
                                            bg="bg.dark"
                                            _checked={{ bg: "purple.400", borderColor: "purple.400" }}
                                        />
                                        <Checkbox.Label fontSize="sm">{m.includeInNotifications}</Checkbox.Label>
                                    </Checkbox.Root>
                                </Box>

                                {error && (
                                    <Box mb={4} p={3} bg="red.900" borderRadius={8} borderColor="red.400" border="1px solid">
                                        <Text fontSize="sm" color="red.400">{error}</Text>
                                    </Box>
                                )}

                                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                    {isEditMode ? (
                                        <Button
                                            size="sm"
                                            colorPalette="red"
                                            variant="outline"
                                            onClick={() => setIsDisconnectOpen(true)}
                                        >
                                            {m.disconnect}
                                        </Button>
                                    ) : (
                                        <Box />
                                    )}
                                    <HStack gap={2}>
                                        <Button variant="outlined" size="sm" onClick={onClose} disabled={isLoading}>
                                            {m.cancel}
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={handleSubmit} loading={isLoading}>
                                            {isEditMode ? m.save : m.connect}
                                        </Button>
                                    </HStack>
                                </HStack>
                            </Dialog.Body>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" position="absolute" top="2" right="2" color="text.grey" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <Dialog.Root open={isDisconnectOpen} onOpenChange={(e) => { if (!e.open) setIsDisconnectOpen(false); }}>
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner p={4}>
                        <Dialog.Content
                            bg="bg.card"
                            color="white"
                            borderColor="border.primary"
                            border="1px solid"
                            w={{ base: "calc(100vw - 2rem)", md: "md" }}
                            maxW="100%"
                            borderRadius={12}
                        >
                            <Dialog.Body pt={6} pb={6} px={6}>
                                <Text fontWeight={700} fontSize="md" mb={3}>{m.disconnectConfirmTitle}</Text>
                                <Text fontSize="sm" color="text.grey" mb={5}>{m.disconnectConfirmBody}</Text>
                                <HStack justify="flex-end" gap={2}>
                                    <Button variant="outlined" size="sm" onClick={() => setIsDisconnectOpen(false)} disabled={isDisconnecting}>
                                        {m.disconnectConfirmCancel}
                                    </Button>
                                    <Button colorPalette="red" size="sm" onClick={handleDisconnect} loading={isDisconnecting}>
                                        {m.disconnectConfirmConfirm}
                                    </Button>
                                </HStack>
                            </Dialog.Body>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </>
    );
};

export default GoogleSheetsIntegrationModal;
